import { FC, useMemo } from 'react';
import { transformStepResponsesForMultiTurn, useMultiTurnInteractionValidation } from '../utils';
import { Modal } from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared-types';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import { useChatStoreExtract } from '../../Chat/hooks/ChatStore.hooks';
import { useChatDemoStore } from './chatDemo.store';
import Editor from '@monaco-editor/react';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

type InteractionDemoResponseViewerProps = {
  featureConfig: IChatTaskFeatureConfig;
};

export const InteractionDemoResponseViewerModal: FC<InteractionDemoResponseViewerProps> = ({
  featureConfig,
}) => {
  const config = useChatStoreExtract(state => state.config);
  const responses = useChatStoreExtract(state => state.responses);
  const { canSubmit } = useMultiTurnInteractionValidation({
    config,
    responses,
    isSubmitting: false,
    isLoading: false,
    hasResponseLoadingError: false,
    workerComments: [],
    reviewLevel: ReviewLevel.Attempt,
    inlineFeedbackConfig: featureConfig.inlineFeedbackConfig,
    allStepsCompleted: true,
  });
  const hideUnmetConditionsEnabled =
    useUnleashFeatureFlag('hide-unmet-conditions-in-transformed-response-enabled', {}, false) ??
    false;

  const transformedResponses = useMemo(
    () => transformStepResponsesForMultiTurn(config, responses, hideUnmetConditionsEnabled),
    [config, responses],
  );

  const isResponseViewerOpen = useChatDemoStore(state => state.isResponseViewerOpen);
  const setResponseViewerOpen = useChatDemoStore(state => state.setResponseViewerOpen);

  return (
    <Modal
      title={canSubmit ? 'Multi-Turn demo interaction is complete!' : 'View interaction response'}
      open={isResponseViewerOpen}
      onClose={() => setResponseViewerOpen(false)}
    >
      <div>
        <Editor
          theme="vs-dark"
          height={720}
          width={720}
          language="json"
          value={JSON.stringify(transformedResponses, null, 2)}
          options={{
            fontSize: 14,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            padding: {
              top: 16,
              bottom: 16,
            },
            formatOnPaste: true,
            formatOnType: true,
            renderLineHighlight: 'none',
          }}
        />
      </div>
    </Modal>
  );
};
